import React, { useMemo, useState } from "react";
import { CircularProgress, InputAdornment, useTheme } from "@mui/material";
import {
  Archive,
  MessageSquare,
  Search,
  Settings,
  Trash2,
  Sparkles,
  Plus,
} from "lucide-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import {
  ConversationDate,
  ConversationItem,
  ConversationItemContent,
  ConversationList,
  ConversationPreview,
  ConversationTitle,
  DeleteIcon,
  EmptyState,
  HeaderAIIconSm,
  HistoryDisabledContainer,
  HistoryDisabledIcon,
  HistoryDisabledSubtitle,
  HistoryDisabledTitle,
  LoadingRow,
  NewChatButton,
  NewChatSection,
  SearchInput,
  SearchSection,
  SettingsButton,
  SidebarFooter,
  SidebarHeader,
  SidebarRoot,
  SidebarTitle,
  SidebarTitleRow,
} from "@components/ChatAgent/components/ChatAgentSidebar/style";
import { ConversationSummary } from "@hooks/useChatAgent";

interface ChatAgentSidebarProps {
  conversations: ConversationSummary[];
  isLoading: boolean;
  activeSessionId: string;
  onSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onDelete: (sessionId: string) => void;
  onSettings?: () => void;
  disabled?: boolean;
  historyEnabled?: boolean;
  awaitingAgentChoice?: boolean;
}

// Formato data per le card di conversazione, allineato al mockup:
//   < 1 minuto fa  → "Adesso"
//   stesso giorno  → "Oggi, HH:mm"
//   ieri           → "Ieri, HH:mm"
//   stesso anno    → "DD MMM, HH:mm" (es. "25 Apr, 10:22")
//   altrimenti     → "DD/MM/YY, HH:mm"
const formatRelativeDate = (iso?: string) => {
  if (!iso) return "";
  const d = dayjs(iso);
  if (!d.isValid()) return "";
  const now = dayjs();
  if (now.diff(d, "minute") < 1) return "Adesso";
  if (d.isSame(now, "day")) return `Oggi, ${d.format("HH:mm")}`;
  if (d.isSame(now.subtract(1, "day"), "day"))
    return `Ieri, ${d.format("HH:mm")}`;
  if (d.isSame(now, "year")) return d.format("DD MMM, HH:mm");
  return d.format("DD/MM/YY, HH:mm");
};

const ChatAgentSidebar = ({
  conversations,
  isLoading,
  activeSessionId,
  onSelect,
  onNewChat,
  onDelete,
  onSettings,
  disabled,
  historyEnabled = true,
  awaitingAgentChoice = false,
}: ChatAgentSidebarProps) => {
  const theme = useTheme();
  const { t } = useTranslation("agent_chat");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const haystack = `${c.title ?? ""} ${c.preview ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [conversations, search]);

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    sid: string,
  ) => {
    e.stopPropagation();
    if (disabled) return;
    onDelete(sid);
  };

  return (
    <SidebarRoot theme={theme}>
      <SidebarHeader>
        <SidebarTitleRow>
          <HeaderAIIconSm>AI</HeaderAIIconSm>
          <SidebarTitle>{t("Olivia")}</SidebarTitle>
        </SidebarTitleRow>
        <NewChatSection>
          <NewChatButton
            fullWidth
            variant="contained"
            disabled={disabled || awaitingAgentChoice}
            endIcon={<Plus size={16} />}
            onClick={onNewChat}
          >
            {t("newChat")}
          </NewChatButton>
        </NewChatSection>
        <AnimatePresence initial={false} mode="wait">
          {historyEnabled && (
            <motion.div
              key="search-section"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
              style={{ overflow: "hidden" }}
            >
              <SearchSection>
                <SearchInput
                  fullWidth
                  size="small"
                  placeholder={t("searchPlaceholder") || ""}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    "& div": {
                      borderRadius: "10px !important",
                    },
                    "& input": {
                      paddingLeft: "0 !important",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
              </SearchSection>
            </motion.div>
          )}
        </AnimatePresence>
      </SidebarHeader>

      <ConversationList withTopBorder={historyEnabled && !awaitingAgentChoice}>
        <AnimatePresence initial={false} mode="wait">
          {awaitingAgentChoice ? (
            <motion.div
              key="awaiting-agent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28 }}
            >
              <HistoryDisabledContainer>
                <HistoryDisabledIcon>
                  <Sparkles size={20} />
                </HistoryDisabledIcon>
                <HistoryDisabledTitle>
                  {t("awaitingAgentTitle")}
                </HistoryDisabledTitle>
                <HistoryDisabledSubtitle>
                  {t("awaitingAgentSubtitle")}
                </HistoryDisabledSubtitle>
              </HistoryDisabledContainer>
            </motion.div>
          ) : !historyEnabled ? (
            <motion.div
              key="history-disabled"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28 }}
            >
              <HistoryDisabledContainer>
                <HistoryDisabledIcon>
                  <Archive size={20} />
                </HistoryDisabledIcon>
                <HistoryDisabledTitle>
                  {t("historyDisabledTitle")}
                </HistoryDisabledTitle>
                <HistoryDisabledSubtitle>
                  {t("historyDisabledSubtitle")}
                </HistoryDisabledSubtitle>
              </HistoryDisabledContainer>
            </motion.div>
          ) : isLoading && conversations.length === 0 ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <LoadingRow>
                <CircularProgress size={18} />
              </LoadingRow>
            </motion.div>
          ) : filtered.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <EmptyState>{t("noConversations")}</EmptyState>
            </motion.div>
          ) : (
            <motion.div
              key="conversation-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <AnimatePresence initial={false}>
                {filtered.map((c) => {
                  const isActive = c.session_id === activeSessionId;
                  const title =
                    c.title?.trim() ||
                    (c.message_count && c.message_count > 0
                      ? t("conversation")
                      : t("newConversation"));
                  const preview = c.preview?.trim() || "";
                  const date = formatRelativeDate(c.updated_at ?? c.created_at);
                  return (
                    <motion.div
                      key={c.session_id}
                      layout
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{
                        layout: { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] },
                        opacity: { duration: 0.16 },
                        scale: { duration: 0.16 },
                      }}
                    >
                      <ConversationItem
                        active={isActive}
                        onClick={() => onSelect(c.session_id)}
                      >
                        <MessageSquare
                          size={18}
                          color={theme.palette.primary.main}
                        />
                        <ConversationItemContent>
                          <ConversationTitle>{title}</ConversationTitle>
                          <ConversationPreview>{preview}</ConversationPreview>
                          {date && <ConversationDate>{date}</ConversationDate>}
                        </ConversationItemContent>
                        {/*
                        // funzionalità ancora non sviluppata lato BE

                        <DeleteIcon
                          size="small"
                          aria-label={t("deleteConversation") || "Delete"}
                          onClick={(e) => handleDelete(e, c.session_id)}
                          disabled={disabled}
                        >
                          <Trash2 size={16} />
                        </DeleteIcon>*/}
                      </ConversationItem>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </ConversationList>

      {onSettings && (
        <SidebarFooter>
          <SettingsButton onClick={onSettings} disabled={disabled}>
            <Settings size={18} />
            {t("settings")}
          </SettingsButton>
        </SidebarFooter>
      )}
    </SidebarRoot>
  );
};

export default ChatAgentSidebar;
